// src/screens/Kiosk.tsx
// Updated: persistent Monri WS + CONFIRM in same session + idle guard (2 min -> countdown -> reset).

import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, message } from "../shims/antd";
import QRCode from "react-qr-code";
import api from "../services/api";
import { useAuthDevice } from "../hooks/pos/useAuthDevice";
import { createOrder, fetchPosCatalog, registerDevice } from "../services/pos";
import { getDeviceSettings } from "../services/deviceSettings";
import { printOrderTicketStyled, type OrderTicketItem } from "../pos/print";
import { autoConfirmOrRejectByInvoice, startCardSale, probeMonry, confirmInvoice, type TerminalEvent } from "../services/paymentTerminal";
import { logoutToPin } from "../services/session";
import { invoke } from "@tauri-apps/api/core";
import { isTauri } from "../utils/tauri";
import DeviceSettingsModal from "../components/DeviceSettingsModal";
import { nextOrderCode } from "../pos/orderSeq";
import { HomeOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { fiscalizeLocal, type SaleItem } from "../pos/fiscal";

type ServiceType = "dine_in" | "takeaway";
type BannerMedia = { id: number; title?: string | null; media_type: "image" | "video"; url: string; duration_ms?: number };

const LS = { serviceType: "kiosk.serviceType", code: "kiosk.lastCode" };

const DEFAULT_BANNERS: BannerMedia[] = [
  { id: -201, title: "Priprema hrane (video 1)", media_type: "video", url: "https://cdn.pixabay.com/video/2020/09/16/50061-459915509_tiny.mp4", duration_ms: 8000 },
  { id: -202, title: "Priprema hrane (video 2)", media_type: "video", url: "https://cdn.pixabay.com/video/2022/07/20/124823-732633111_large.mp4", duration_ms: 9000 },
  { id: -103, title: "Kafa i topli napici", media_type: "image", url: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=1920&q=80", duration_ms: 7000 },
  { id: -104, title: "Pizza & snack ponuda", media_type: "video", url: "https://cdn.coverr.co/videos/coverr-pizza-with-tomatoes-0261/1080p.mp4", duration_ms: 7000 },
];

const ADMIN_URL =
  (window as any).__ADMIN_URL__ ??
  localStorage.getItem("admin_panel_url") ??
  "https://portal.itable.app";

const PROGRESS_TEXT: Record<string, string> = {
  "1": "Obrada u toku…",
  "3": "Molimo potvrdite na terminalu…",
  "4": "Čekamo odgovor izdavaoca…",
  "17": "Kartica očitana",
  "18": "Štampanje na terminalu…",
  "_PIN": "Unesite PIN na terminalu…",
};

export default function Kiosk() {
  const alreadyPaired = !!localStorage.getItem("device_uuid");
  const [pairOpen, setPairOpen] = useState(!alreadyPaired);
  const [pairCode, setPairCode] = useState<string>("");
  const [checking, setChecking] = useState(false);

  // tajni settings
  const [openSettings, setOpenSettings] = useState(false);
  const secretClicksRef = useRef(0);
  const secretTimerRef = useRef<any>(null);

  const getDeviceCode = async () => {
    if (isTauri()) {
      try { return await invoke<string>("get_machine_code"); } catch { return "TAURI_ERR"; }
    }
    let code = localStorage.getItem("tv.uuid_device_code");
    if (!code) {
      code = "WEB" + Math.random().toString(36).slice(2, 8).toUpperCase();
      localStorage.setItem("tv.uuid_device_code", code);
    }
    return code;
  };

  useEffect(() => {
    let stop = false;
    (async () => {
      const code = await getDeviceCode();
      if (!stop && code) {
        setPairCode(code as any);
        localStorage.setItem("kiosk.device_uuid_paring", code as any);
      }
    })();
    return () => { stop = true; };
  }, []);

  const pairLink = pairCode
    ? `${ADMIN_URL}/pair-device?code=${encodeURIComponent(pairCode)}&kind=kiosk&server=${encodeURIComponent(window.location.origin)}`
    : "";

  useEffect(() => {
    if (!pairOpen || !pairCode) return;
    let stop = false;
    async function tick() {
      try {
        setChecking(true);
        const resp = await api.get(`/pos/device/pairing/status?code=${encodeURIComponent(pairCode)}`);
        const json = resp?.data || {};
        if (json?.status === "completed" && json?.uuid) {
          localStorage.setItem("device_uuid", json.uuid);
          message.success("Uređaj uspješno uparen.");
          setPairOpen(false);
        }
      } catch {} finally { setChecking(false); }
      if (!stop) setTimeout(tick, 2000);
    }
    tick();
    return () => { stop = true; };
  }, [pairOpen, pairCode]);

  const deviceToken = useAuthDevice({ autoLoginDeviceOnly: true } as any);

  const hotCorner = (
    <div
      style={{ position: "fixed", top: 0, right: 0, width: 60, height: 60, zIndex: 99999 }}
      onClick={() => {
        if (secretTimerRef.current) clearTimeout(secretTimerRef.current);
        secretClicksRef.current = (secretClicksRef.current || 0) + 1;
        secretTimerRef.current = setTimeout(() => { secretClicksRef.current = 0; }, 5000);
        if (secretClicksRef.current >= 10) { secretClicksRef.current = 0; setOpenSettings(true); }
      }}
    />
  );

  return (
    <div style={{ zIndex: 9999 }}>
      {hotCorner}
      <DeviceSettingsModal open={openSettings} onClose={() => setOpenSettings(false)} canEdit />
      {deviceToken ? <KioskPaired /> : <div style={{ padding: 16 }}><h3>Spajanje uređaja...</h3></div>}

      <Modal
        open={pairOpen}
        closable={false}
        maskClosable={false}
        style={{ zIndex: 9999 }}
        title="Povezivanje uređaja (Kiosk)"
        footer={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Button danger onClick={() => setPairOpen(false)}>Otkaži uparivanje</Button>
              <Button onClick={() => pairLink ? window.open(pairLink, "_blank") : null} disabled={!pairLink}>
                {pairLink ? "Otvori link na ovom uređaju" : "Učitavanje koda..."}
              </Button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => { localStorage.clear(); window.location.reload(); }}>Nazad</Button>
              <Button danger onClick={() => { localStorage.clear(); window.location.reload(); }}>Očisti podatke i restart</Button>
            </div>
            <div style={{ width: "100%", marginTop: 8, fontSize: 12, opacity: 0.75, textAlign: "right" }}>
              {checking ? "Provjera..." : (pairLink ? "Skeniraj QR kod ili otvori link na drugom uređaju." : "Priprema koda uređaja...")}
            </div>
          </div>
        }
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div>📱 Na telefonu (ulogovan u Admin-FE) skeniraj QR i dodijeli ovaj uređaj.</div>
          <div style={{ background: "#fff", padding: 12, borderRadius: 12, display: "grid", placeItems: "center" }}>
            <QRCode value={pairLink || "loading"} size={256} />
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, wordBreak: "break-all" }}>
            {pairLink || "Učitavanje koda..."}
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ====================== KioskPaired (glavni UI) ====================== */

function KioskPaired() {
  const [step, setStep] = useState<"landing" | "menu" | "payment" | "done">("landing");
  const [countdown, setCountdown] = useState(20);
  const [lastNumber, setLastNumber] = useState<string>("");

  // device settings
  const [printer, setPrinter] = useState<{ ip?: string; share?: string }>({});
  const [outletId, setOutletId] = useState<number>(0);
  const [serviceType, setServiceType] = useState<ServiceType>(() => (localStorage.getItem(LS.serviceType) as ServiceType) || "takeaway");

  // Monri konekcija
  const [terminalReady, setTerminalReady] = useState<boolean>(false);
  const [checkingTerminal, setCheckingTerminal] = useState<boolean>(false);

  // dizajn kioska
  const [design, setDesign] = useState({ gridCols: 3, itemImgH: 160, catImgS: 64, leftCol: "320px", bgColor: "#0b0f1a", textColor: "#ffffff", buttonColor: "#ffd60a" });

  // bannere / katalog
  const [banners, setBanners] = useState<BannerMedia[]>(DEFAULT_BANNERS);
  const [bix, setBix] = useState(0);
  const [categories, setCategories] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [activeCat, setActiveCat] = useState<number | null>(null);

  // Korpa + bottom-sheet
  const [cart, setCart] = useState<any[]>([]);
  const [cartOpen, setCartOpen] = useState(false);

  // Monri status overlay
  const [paying, setPaying] = useState(false);
  const [monry, setMonry] = useState<{ code?: string; text?: string; invoice?: string } | null>(null);

  // ===== Idle guard (2 min) =====
  const [idleOpen, setIdleOpen] = useState(false);
  const [idleLeft, setIdleLeft] = useState(15);
  const idleRef = useRef<number | null>(null);
  const warnRef = useRef<number | null>(null);

  const resetToLanding = () => {
    setCart([]);
    setStep("landing");
    setIdleOpen(false);
    setIdleLeft(15);
  };

  useEffect(() => {
    const bump = () => {
      if (warnRef.current) { clearInterval(warnRef.current); warnRef.current = null; }
      setIdleOpen(false); setIdleLeft(15);
      if (idleRef.current) clearTimeout(idleRef.current);
      idleRef.current = window.setTimeout(() => {
        setIdleOpen(true);
        setIdleLeft(15);
        warnRef.current = window.setInterval(() => {
          setIdleLeft((s) => {
            if (s <= 1) {
              clearInterval(warnRef.current!);
              warnRef.current = null;
              resetToLanding();
              return 0;
            }
            return s - 1;
          });
        }, 1000);
      }, 120_000); // 2 min
    };
    const evs = ["pointerdown","keydown","mousemove","touchstart"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true } as any));
    bump();
    return () => {
      evs.forEach((e) => window.removeEventListener(e, bump));
      if (idleRef.current) clearTimeout(idleRef.current);
      if (warnRef.current) clearInterval(warnRef.current);
    };
  }, []);

  useEffect(() => { localStorage.setItem(LS.serviceType, serviceType); }, [serviceType]);

  // INIT
  useEffect(() => {
    (async () => {
      try {
        const s = await getDeviceSettings([
          "device_prefix","printer_ip","printer_share","kiosk_banners_json",
          "kiosk_grid_cols","kiosk_item_img_h","kiosk_cat_img_s","kiosk_left_ratio","kiosk_color_text","kiosk_color_bg","kiosk_color_button",
          "monry_ws_host","monry_ws_port"
        ]);
        setPrinter({ ip: s["printer_ip"] || undefined, share: s["printer_share"] || undefined });
        try { const dev = JSON.parse(localStorage.getItem("device") || "{}"); setOutletId(Number(dev?.outlet_id || 0)); } catch {}

        if (s["kiosk_banners_json"]) {
          try {
            const arr = JSON.parse(String(s["kiosk_banners_json"]));
            if (Array.isArray(arr) && arr.length) {
              setBanners(arr.map((x: any, i: number) => ({ id: i + 1, media_type: (x.media_type === "video" || x.media_type === "image") ? x.media_type : "image", url: String(x.url), title: x.title, duration_ms: x.duration_ms })));
            }
          } catch {}
        }

        const gridCols = Math.max(1, Math.min(6, Number(s["kiosk_grid_cols"] ?? 3)));
        const itemImgH = Math.max(120, Math.min(280, Number(s["kiosk_item_img_h"] ?? 160)));
        const catImgS  = Math.max(48, Math.min(120, Number(s["kiosk_cat_img_s"] ?? 64)));
        const leftRatio = Number(s["kiosk_left_ratio"] ?? 28);
        const leftCol = Number.isFinite(leftRatio) ? `${leftRatio}%` : "320px";
        const bgColor = String(s["kiosk_color_bg"] || "#0b0f1a");
        const textColor = String(s["kiosk_color_text"] || "#ffffff");
        const buttonColor = String(s["kiosk_color_button"] || "#ffd60a");
        setDesign({ gridCols, itemImgH, catImgS, leftCol, bgColor, textColor, buttonColor });
      } catch {}

      try {
        const byCat = await fetchPosCatalog();
        setCategories(byCat.map((x: any) => x.category));
        const flat = byCat.flatMap((x: any) => x.products);
        setProducts(flat);
        setActiveCat(byCat[0]?.category?.id || null);
      } catch {
        localStorage.removeItem("device_token");
        localStorage.removeItem("device");
      }
    })();
  }, []);

  // Rotacija bannera
  useEffect(() => {
    if (step !== "landing" || banners.length === 0) return;
    const dur = Math.max(5000, Math.min(20000, banners[bix]?.duration_ms || 9000));
    const t = setTimeout(() => setBix((p) => (p + 1) % banners.length), dur);
    return () => clearTimeout(t);
  }, [step, banners, bix]);

  // total + helpers
  const sumMods = (i: any) => (Array.isArray(i.modifiers) ? i.modifiers : []).reduce((s: number, m: any) => s + Number(m?.price || m?.price_delta || 0), 0) * Number(i.quantity || 1);
  const total = useMemo(() => cart.reduce((s, i) => s + (Number(i.unit_price || i.price || 0) * Number(i.quantity || 1)) + sumMods(i), 0), [cart]);

  const asOrderItems = (c: any[]) =>
    c.map((i) => ({
      product_id: Number(i.product_id),
      quantity: Number(i.quantity || 1),
      unit_price: Number(i.unit_price || i.price || 0),
      modifiers: (Array.isArray(i.modifiers) ? i.modifiers : []).map((m: any) => ({ modifier_id: Number(m.id), price: Number(m.price || m.price_delta || 0) })),
    }));

  const printTicket = async (code: string, cartItems: any[], extras?: { fiscalNumber?: string }) => {
    try {
      const ticketItems: OrderTicketItem[] = cartItems.map((i) => ({
        name: String(i.name || `#${i.product_id}`),
        qty: Number(i.quantity || 1),
        modifiers: (i.modifiers || []).map((m: any) => String(m?.name || "")),
      }));
      if (printer.ip || printer.share) {
        const device = JSON.parse(localStorage.getItem("device") || "{}");
        const employee = JSON.parse(localStorage.getItem("employee") || "{}");
        await printOrderTicketStyled(
          {
            clientName: device?.name || "iTable Kiosk",
            orderId: code,
            workerName: employee?.name || undefined,
            verifyBaseUrl: "https://portal.itable.app/order-check",
            fiscalNumber: extras?.fiscalNumber,
          },
          ticketItems,
          { printerIp: printer.ip, printerShare: printer.share }
        );
      }
    } catch (e) { console.warn("Kiosk print ticket fail:", e); }
  };

  const cartToFiscalItems = (c: any[]): SaleItem[] =>
    c.map((i) => ({
      name: String(i.name || `#${i.product_id}`),
      unit: "kom",
      price: Number(i.unit_price || 0) + (Array.isArray(i.modifiers) ? i.modifiers.reduce((s: number, m: any) => s + Number(m.price || 0), 0) : 0),
      vat: "E",
      qty: Number(i.quantity || 1),
      discount: 0,
    }));

  // Monri WS ping + cleanup pending on entering payment
  useEffect(() => {
    let on = true;
    async function initMonri() {
      setCheckingTerminal(true);
      try {
        const pending = localStorage.getItem("monry.pending.invoice");
        if (pending) {
          try {
            await confirmInvoice(pending, false);
          } catch {}
          localStorage.removeItem("monry.pending.invoice");
        }
      } finally {
        const ok = await probeMonry();
        if (on) { setTerminalReady(ok); setCheckingTerminal(false); }
      }
    }
    if (step === "payment") initMonri();
    return () => { on = false; };
  }, [step]);

  // DONE countdown
  useEffect(() => {
    if (step !== "done") return;
    setCountdown(20);
    const t = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : 0)), 1000);
    const back = setTimeout(() => setStep("landing"), 20000);
    setServiceType("dine_in");
    return () => { clearInterval(t); clearTimeout(back); };
  }, [step]);

  const goPay = () => {
    if (!cart.length) return message.warning("Dodajte artikle u korpu");
    setCartOpen(false);
    setStep("payment");
  };

  async function payCash() {
    try {
      const need = Number(total) || 0;
      if (need <= 0) return message.warning("Korpa je prazna.");
      const number = await nextOrderCode();
      localStorage.setItem(LS.code, number);

      const tag = serviceType === "dine_in" ? "U objektu" : "Za ponijeti";

      const payload: any = {
        outlet_id: outletId || 0,
        number,
        items: asOrderItems(cart),
        payment_type: "cash",
        payment_status: "unpaid",
        note: tag,
      };
      await createOrder(payload);

      await printTicket(number, cart);

      setLastNumber(number);
      setCart([]);
      setStep("done");
      message.success("Narudžba kreirana. Platite na blagajni.");
    } catch (e: any) {
      message.error(e?.message || "Greška pri gotovinskom plaćanju");
    }
  }

  const settledRef = useRef(false);

  async function payCard() {
    try {
      const need = Number(total) || 0;
      if (need <= 0) return message.warning("Korpa je prazna.");
      if (!terminalReady) { message.error("Kartično plaćanje nije dostupno — terminal offline."); return; }

      settledRef.current = false;
      const number = await nextOrderCode();
      localStorage.setItem(LS.code, number);
      setPaying(true);
      setMonry({ text: "Molimo prislonite karticu…", code: undefined });

      // zapamti potencijalni invoice lokalno
      localStorage.setItem("monry.pending.invoice", number);

      const { result } = await startCardSale(need, number, (ev: TerminalEvent) => {
        if (settledRef.current) return;
        if (ev?.type === "PROGRESS") {
          const code = String(ev.code || "");
          const t = PROGRESS_TEXT[code] || (code ? `Status ${code}…` : ev.message || "Obrada…");
          const pinLike = code === "" && (ev.message || "").toLowerCase().includes("pin");
          setMonry({ code, text: pinLike ? PROGRESS_TEXT["_PIN"] : t });
        }
      });

      if (result.type === "ERROR" && result.code === "1001") {
        const invoice = (result as any).invoice || number;
        localStorage.setItem("monry.pending.invoice", String(invoice));
        setMonry({ code: "1001", text: "Čeka potvrdu…", invoice });

        const conf = await autoConfirmOrRejectByInvoice(invoice, need);
        if (conf.type !== "APPROVED") {
          localStorage.removeItem("monry.pending.invoice");
          message.error((conf as any).message || "CONFIRM nije odobren");
          settledRef.current = true;
          setPaying(false);
          const ok = await probeMonry(); setTerminalReady(ok);
          return;
        }

        // fiskalizacija + štampa + kreiraj order
        localStorage.removeItem("monry.pending.invoice");
        const saleItems = cartToFiscalItems(cart);
        const fx = await fiscalizeLocal(saleItems, [{ type: "Kartica", amount: need }]);
        if (fx?.vrsta && fx.vrsta !== "OK") {
          message.error(fx.values?.OpisGreske || fx.values?.Poruka || "Fiskalni server: greška");
          settledRef.current = true;
          setPaying(false);
          return;
        }
        const fiscalNumber = fx?.values?.BrojFiskalnogRacuna;

        await createOrder({
          outlet_id: outletId || 0,
          number,
          items: asOrderItems(cart),
          payment_type: "card",
          payment_status: "paid",
        });

        await printTicket(number, cart, { fiscalNumber });

        settledRef.current = true;
        setPaying(false);
        setLastNumber(number); setCart([]); setStep("done");
        message.success("Uspješno plaćeno karticom.");
        const ok = await probeMonry(); setTerminalReady(ok);
        return;
      }

      if (result.type === "APPROVED") {
        // fallback approved path (ako nema 1001)
        const saleItems = cartToFiscalItems(cart);
        const fx = await fiscalizeLocal(saleItems, [{ type: "Kartica", amount: need }]);
        const fiscalNumber = fx?.values?.BrojFiskalnogRacuna;
        await createOrder({ outlet_id: outletId || 0, number, items: asOrderItems(cart), payment_type: "card", payment_status: "paid" });
        await printTicket(number, cart, { fiscalNumber });
        settledRef.current = true; setPaying(false); setLastNumber(number); setCart([]); setStep("done");
        message.success("Plaćanje odobreno.");
        const ok = await probeMonry(); setTerminalReady(ok);
        return;
      }

      // Declined/error
      localStorage.removeItem("monry.pending.invoice");
      setPaying(false);
      message.error("Kartično plaćanje odbijeno ili greška.");

    } catch (e) {
      setPaying(false);
      message.error("Greška pri kartičnom plaćanju.");
    }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: `${design.leftCol} 1fr`, height: "100vh", background: design.bgColor, color: design.textColor }}>
      {/* Left column */}
      <div style={{ padding: 16, display: "grid", gridTemplateRows: "auto 1fr auto", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <HomeOutlined />
          <div style={{ fontWeight: 700 }}>iTable Kiosk</div>
        </div>

        {/* Landing banners */}
        {step === "landing" && (
          <div style={{ height: "100%", borderRadius: 16, overflow: "hidden", background: "#111" }}>
            {banners[bix]?.media_type === "video" ? (
              <video src={banners[bix].url} autoPlay muted loop style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <img src={banners[bix]?.url} alt={banners[bix]?.title || ""} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}
          </div>
        )}

        {/* Footer controls */}
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="primary" onClick={() => setStep("menu")}>Počni narudžbu</Button>
          <Button onClick={() => setStep("payment")} disabled={!cart.length}>Naplata</Button>
          <Button danger onClick={() => { setCart([]); setStep("landing"); }}>Reset</Button>
        </div>
      </div>

      {/* Right column (menu/payment/done) */}
      <div style={{ padding: 16 }}>
        {step === "menu" && (
          <div style={{ display: "grid", gridTemplateRows: "auto 1fr auto", gap: 12, height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ShoppingCartOutlined />
              <div style={{ fontWeight: 700 }}>Meni</div>
              <div style={{ marginLeft: "auto" }}>
                <Button onClick={() => setStep("payment")} type="primary" disabled={!cart.length}>Kasa ({cart.length})</Button>
              </div>
            </div>
            <div style={{ overflow: "auto" }}>
              {/* minimalistički grid — ovdje pretpostavljamo products state */}
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${design.gridCols}, minmax(0,1fr))`, gap: 12 }}>
                {products.map((p: any) => (
                  <div key={p.id} onClick={() => setCart((cur) => [...cur, {
                    product_id: p.id, name: p.name, unit_price: Number(p.price||0), quantity: 1, modifiers: [], image: p.image||null
                  }])} style={{ background: "#1f2937", borderRadius: 12, padding: 12, cursor: "pointer" }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{p.name}</div>
                    <div>{Number(p.price || 0).toFixed(2)} KM</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button onClick={() => setStep("landing")}>Nazad</Button>
              <div style={{ marginLeft: "auto" }}>
                <Button onClick={() => setCartOpen(!cartOpen)}>{cartOpen ? "Sakrij korpu" : "Korpa"} ({cart.length})</Button>
                <Button type="primary" onClick={goPay} disabled={!cart.length}>Kasa</Button>
              </div>
            </div>
            {cartOpen && (
              <div style={{ marginTop: 8, background: "#0f172a", borderRadius: 12, padding: 12 }}>
                {cart.map((i, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                    <div style={{ flex: 1 }}>{i.name}</div>
                    <div>x{i.quantity}</div>
                    <div>{(Number(i.unit_price) * Number(i.quantity)).toFixed(2)} KM</div>
                    <Button size="small" onClick={() => setCart((c) => c.map((x, j) => j===idx ? { ...x, quantity: Math.max(1, Number(x.quantity||1)-1) } : x))}>-</Button>
                    <Button size="small" onClick={() => setCart((c) => c.map((x, j) => j===idx ? { ...x, quantity: Number(x.quantity||1)+1 } : x))}>+</Button>
                    <Button size="small" danger onClick={() => setCart((c) => c.filter((_, j) => j!==idx))}>Ukloni</Button>
                  </div>
                ))}
                <div style={{ textAlign: "right", fontWeight: 700, marginTop: 8 }}>Ukupno: {total.toFixed(2)} KM</div>
              </div>
            )}
          </div>
        )}

        {step === "payment" && (
          <div style={{ display: "grid", gridTemplateRows: "auto 1fr auto", height: "100%", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 700 }}>Plaćanje</div>
              <div style={{ marginLeft: "auto", opacity: .8 }}>
                {checkingTerminal ? "Provjera terminala…" : (terminalReady ? "Terminal spreman" : "Terminal offline")}
              </div>
            </div>
            <div style={{ display: "grid", placeItems: "center" }}>
              {!paying ? (
                <div style={{ display: "flex", gap: 12 }}>
                  <Button onClick={payCash} disabled={!cart.length}>Gotovina</Button>
                  <Button type="primary" onClick={payCard} disabled={!cart.length || !terminalReady}>Kartica</Button>
                </div>
              ) : (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 18, marginBottom: 8 }}>{monry?.text || "Obrada…"}</div>
                  <div>Molimo ne uklanjajte karticu dok ne završimo.</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={() => setStep("menu")}>Nazad</Button>
              <Button danger onClick={() => { setCart([]); setStep("landing"); }}>Otkaži</Button>
            </div>
          </div>
        )}

        {step === "done" && (
          <div style={{ display: "grid", placeItems: "center", height: "100%" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Hvala!</div>
              <div style={{ opacity: .85, marginBottom: 8 }}>Vaš broj: <b>#{lastNumber}</b></div>
              <div>Automatski se vraćamo na početni ekran za {countdown}s.</div>
            </div>
          </div>
        )}
      </div>

      {/* Idle overlay */}
      <Modal open={idleOpen} footer={null} closable={false} maskClosable onCancel={() => setIdleOpen(false)}>
        <div style={{ textAlign:"center", padding:16 }}>
          <h2>Da li ste još tu?</h2>
          <p>Dodirnite dugme da nastavite. U suprotnom vraćamo se na početni ekran za {idleLeft}s.</p>
          <Button size="large" type="primary" onClick={() => setIdleOpen(false)}>
            Nastavi ({idleLeft})
          </Button>
          <div style={{ marginTop: 8, opacity: .7, fontSize: 12 }}>
            Bez interakcije ćemo otkazati tekuću sesiju radi sljedećeg korisnika.
          </div>
        </div>
      </Modal>
    </div>
  );
}